import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import ClassIcon from '../icons/ClassIcon';
import SchoolIcon from '../icons/SchoolIcon';
import PositionIcon from '../icons/PositionIcon';
import HomeIcon from '../icons/HomeIcon';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import EmailIcon from '../icons/EmailIcon';
import PhoneIcon from '../icons/PhoneIcon';
import MapIcon from '../icons/MapIcon';
import CoachIcon from '../icons/CoachIcon';
import AgentIcon from '../icons/AgentIcon';
import TrainerIcon from '../icons/TrainerIcon';
import OffersIcon from '../icons/OffersIcon';
import HandshakeIcon from '../icons/HandshakeIcon';
import StarIcon from '../icons/StarIcon';
import LinkIcon from '../icons/LinkIcon';
import JerseyIcon from '../icons/JerseyIcon';
import ThreeX from '../icons/ThreeX';
import TypesIcon from '../icons/TypesIcon';
import RugbyIcon from '../icons/RugbyIcon';
import UserCircleIcon from '../icons/UserCircleIcon';
import RecruitingIcon from '../icons/RecruitingIcon';
import ContactCardIcon from '../icons/ContactCardIcon';
import ReportCardIcon from '../icons/ReportCardIcon';
import TFPillLogoIcon from '../icons/TFPillLogoIcon';
import TFVLogoIcon from '../icons/TFVLogoIcon';
import PAIFilledIcon from '../icons/PAIFilledIcon';
import COMFilledIcon from '../icons/COMFilledIcon';
import MPHFilledIcon from '../icons/MPHFilledIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import CanadaIcon from '../icons/CanadaIcon';
import GlobeIcon from '../icons/GlobeIcon';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import Stars from '../atoms/Stars';
import TeamLogo from '../atoms/TeamLogo';
import NotAvailable from '../atoms/NotAvailable';
import Tooltip from '../atoms/Tooltip';
import Loader from '../atoms/Loader';
import Offers from '../molecules/Offers';
import Links from '../molecules/Links';
import { inchesToFeetAndInches, datetimeToDate } from '../services/converter';
import { isValidDOB, objectWithMaxCombine, getMostRecentDataByProperty, prepareLinks } from '../services/player';
import gql from '../services/gql';
import { COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_BLUE, COLOR_DARK_BLUE, COLOR_BORDER, COLOR_BLACK, COLOR_ORANGE, COLOR_DARK_GRAY, COLOR_WHITE, COLOR_GREEN } from '../styles/colors';
import Player from '../types/Player';
import Position from '../types/Position';
import HighSchoolCombine from '../types/HighSchoolCombine';
import HighSchool from '../types/HighSchool';
import Team from '../types/Team';
import { LinkType } from '../types/Link';
import On3LogoIcon from '../icons/On3LogoIcon';
import NilValuesTooltip from '../molecules/NilValuesTooltip';
import { PlayerNILValues } from '../types/PlayerNILValues';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  Grid,
  Typography,
} from '@material-ui/core';
import ListItem from '../atoms/ListItem';
import RecruitingContent from '../molecules/RecruitingContent';


interface UpdatedPlayerProfileHighSchoolOverviewProps {
  className?: string;
  player?: Player;
  selectedPosition?: Position;
  playerNilValues?: PlayerNILValues[];
  playerOn3Date?: string;
}

const BETTER_THAN_TOOLTIP_TEXT = 'Better than D-I position';

const useStyles = makeStyles(theme => ({
  playerProfileHighSchoolOverview: {
  },
  headerClassName: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  headerName: {
    flexGrow: 1,
    paddingLeft: theme.spacing(2),
    fontWeight: 700,
  },
  actionWrap: {
    '& > button': {
      backgroundColor: 'inherit',
      borderRadius: 0,
      borderLeft: `1px solid ${COLOR_BORDER}`,
      boxShadow: 'none',
      color: COLOR_BLUE,
      padding: theme.spacing(2),

      '& > span': {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 700,
        textDecoration: 'underline',
        textTransform: 'capitalize',
      },

      '& .MuiSvgIcon-root': {
        color: COLOR_BLACK,
        fontSize: '28px',
        fontWeight: 'normal',
      },
    }
  },
  actionIcon: {
    display: 'flex',
    maxWidth: '28px',
    
    '& svg': {
      height: 'auto',
    }
  },
  column: {
    borderRight: `1px solid ${theme.palette.divider}`,
    
    "&:last-child": {
      borderRight: "none",
    },
  },
  listItem: {
    padding: theme.spacing(.5, 2.5),
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1.2,

    '& a': {
      color: COLOR_BLUE
    }
  },
  progressLine: {
    maxWidth: '350px',
    marginLeft: 'auto',
    cursor: 'help',
  },
  chip: {
    borderWidth: '2px',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    height: 26,
  },
  overviewChip: {
    color: COLOR_WHITE,
  },
  chipWithLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipLabel: {
    fontWeight: 700,
  },
  badges: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 0),
    alignItems: 'center',
    width: '100%',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    flexBasis: '33.3333%',
    gap: theme.spacing(.5),
    paddingRight: theme.spacing(1),
    fontWeight: 700,

    '&:last-child': {
      paddingRight: 0,
    },
  },
  badgeIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  badgeIconOrange: {
    color: COLOR_ORANGE
  },
  badgeIconGreen: {
    color: '#69BD28'
  },
  badgeIconGray: {
    color: COLOR_DARK_GRAY
  },
  dividedContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(.5, 2),
  },
  dividedContentSection: {
    display: 'flex',
    alignItems: 'center',
    minHeight: '30px',
    paddingRight: theme.spacing(2),
    borderRight: `1px solid ${COLOR_MIDDLE_GRAY}`,

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },
  },
  locationIcon: {
    width: '26px'
  },
  committedTeam: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  committedTeamLogo: {
    width: 'auto',
    height: '32px',
  },
  tfvValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tfvIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
      
    '& svg': {
      width: '100%',
      height: 'auto',
    },
  },
  leftMargin: {
    marginLeft: '5px',
  },
  socialProfile: {
    marginLeft: theme.spacing(3),

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
}), { name: UpdatedPlayerProfileHighSchoolOverview.name });

export default function UpdatedPlayerProfileHighSchoolOverview(props: UpdatedPlayerProfileHighSchoolOverviewProps) {
  const {
    className,
    player,
    selectedPosition,
    playerNilValues = [],
    playerOn3Date = ''
  } = props;
  const classes = useStyles();

  const [loading, setLoading] = useState<boolean>(false);
  const [committedToTeam, setCommittedToTeam] = useState<Team>();
  const [isRecruitingDialogOpen, setIsRecruitingDialogOpen] = useState<boolean>(false);
  const [isContactPlayerDialogOpen, setIsContactPlayerDialogOpen] = useState<boolean>(false);
  const primarySchool = ((player && player.highSchools) || [])
    .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);

  const heightPercentile: any = primarySchool && primarySchool.heightPerc && (primarySchool.heightPerc
    .find((heightPerc: any) => heightPerc && heightPerc.code === selectedPosition) || {} as any).percentile;
  const weightPercentile: any = primarySchool && primarySchool.weightPerc && (primarySchool.weightPerc
    .find((weightPerc: any) => weightPerc && weightPerc.code === selectedPosition) || {} as any).percentile;

  const highSchoolCombines: HighSchoolCombine[] = (player && player.hsCombines) || [];
  const wingspan = getMostRecentDataByProperty(highSchoolCombines, 'wingspan');
  const arm = getMostRecentDataByProperty(highSchoolCombines, 'arm');
  const hand = getMostRecentDataByProperty(highSchoolCombines, 'hand');
  const on3PublicNILVal = player?.on3PublicNILValuation || null;

  const stars = (typeof player?._247Star === 'number' || typeof player?.compStar === 'number')
    ? player?._247Star || player?.compStar || 0
    : null;
  const dob = !!player?.dob && datetimeToDate(player.dob);
  const links = prepareLinks(player);
  const ssaLink = (links?.find(link => link.key === LinkType.ssa) || {})?.value;
  const socialLinks = links.filter(link => link.key === LinkType.instagram || link.key === LinkType.twitter);
  const allLinks = links.filter(link => link.key !== LinkType.instagram && link.key !== LinkType.twitter);

  useEffect(() => {
    if (player?.committedToTeamId) {
      fetchCommittedToTeam(player.committedToTeamId);
    }
  }, [player?.committedToTeamId]);

  // console.log('primarySchool: ', primarySchool, player);

  function fetchCommittedToTeam(teamId: number) {
    setLoading(true);

    gql(`
        team (id: ${teamId}) {
          name
          logo247
          logoESPN
          logoAlt
        }
      `)
      .then((data: any) => data.team as Team)
      .then((team: Team) => setCommittedToTeam(team))
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  // function recruitingContent() {
  //   return (
  //     <>
  //     <NamedBox name='Recruiting'>
  //       <ListItem
  //         icon={ClassIcon}
  //         title='High School Class:'
  //       >
  //         <div><b>{(primarySchool && primarySchool.graduatingClass) || 0}</b></div>
  //       </ListItem>
  //       <ListItem
  //         icon={SchoolIcon}
  //         title='School:'
  //       >
  //         <div>
  //           <b>{(primarySchool?.name || 'N/A')}</b>
  //           <strong>GPA:</strong> {player?.gpa || 'N/A'}&nbsp;&nbsp;
  //           <strong>SAT:</strong> {player?.sat || 'N/A'}&nbsp;&nbsp;
  //           <strong>ACT:</strong> {player?.act || 'N/A'}
  //           </div>
  //       </ListItem>
  //       <ListItem
  //         icon={OffersIcon}
  //         title='Offers:'
  //       >
  //         <div>
  //           <b>{(player?.offers?.length || 0).toString()}</b>
  //         </div>
  //       </ListItem>
  //       <ListItem
  //         icon={OffersIcon}
  //         title='Offer Quality Score:'
  //       >
  //         <div>
  //           <ProgressLine className={classes.progressLine} value={92} />
  //           <b>92%</b>
  //         </div>
  //       </ListItem>
  //       <ListItem
  //         icon={HandshakeIcon}
  //         title='Committed:'
  //       >
  //         <div>
  //           <b>{committedToTeam ? committedToTeam.name : 'Uncommitted'}</b>
  //         </div>
  //       </ListItem>
  //       <ListItem
  //         icon={StarIcon}
  //         title='Star Rating:'
  //       >
  //         <div>
  //           <Stars value={player?._247Star ?? player?.compStar ?? 0} />
  //         </div>
  //       </ListItem>
  //       <ListItem
  //         icon={StarIcon}
  //         title='Industry Rating:'
  //       >
  //         <div>
  //           <b>{player?._247Comp}</b>
  //         </div>
  //       </ListItem>
  //       <ListItem
  //         icon={StarIcon}
  //         title='State Rank:'
  //       >
  //         <div>
  //           <b>#7 in Illinois</b>
  //         </div>
  //       </ListItem>
  //       <ListItem
  //         icon={StarIcon}
  //         title='Pos Rank:'
  //       >
  //         <div>
  //           <b>QB: #18 Nationally</b>
  //         </div>
  //       </ListItem>
  //       <ListItem title=''>
  //         <Box mt={3} display="flex" flexWrap="wrap" alignItems="center" gridGap={16}>
  //           <Box>
  //             <Typography variant="body2">
  //               <strong>PREP REDZONE</strong>
  //             </Typography>
  //             <Typography variant="body2" variantMapping={{ body2: 'div' }}>
  //               State Rank: 5
  //             </Typography>
  //             <Typography variant="body2" variantMapping={{ body2: 'div' }}>
  //               Position Rank: 5
  //             </Typography>
  //           </Box>
  //         </Box>
  //       </ListItem>
  //       <ListItem title=''>
  //         {(!!links?.length) && (
  //           <ListItem
  //             contentClassName={classes.links}
  //             // icon={LinkIcon}
  //             title=''
  //           >
  //             <Links links={links} />
  //           </ListItem>
  //         )}
  //       </ListItem>
  //     </NamedBox>
  //     </>
  //   )
  // }

  function contactPlayerContent() {
    return (
      <NamedBox
      name={`Contact ${player?.firstName} ${player?.lastName}`}
      >
        {(player && !!player.email) && (
          <ListItem
            icon={EmailIcon}
            title='Player Contact:'
            className={classes.listItem}
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b><a href="mailto:{player.email}">{player.email}</a></b>
              </div>

              <div className={classes.dividedContentSection}>
                <Links links={socialLinks} linkClassName={classes.socialProfile} />
              </div>
            </div>
          </ListItem>
        )}

        {(player && !!player.cellPhone) && (
          <ListItem
            icon={PhoneIcon}
            title='Player Phone:'
            className={classes.listItem}
          >
            <b>{player.cellPhone}</b>
          </ListItem>
        )}

        {(player && (player.address || player.cityStateZip)) && (
          <ListItem
            icon={MapIcon}
            title='Address:'
            className={classes.listItem}
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>{player.address || 'N/A'}</b>
              </div>

              {player?.cityStateZip && (
                <div className={classes.dividedContentSection}>
                  <b>{player?.cityStateZip}</b>
                </div>
              )}
            </div>
          </ListItem>
        )}

        <ListItem
          icon={UserCircleIcon}
          title='Parent Name(s):'
          className={classes.listItem}
        >
          <div className={classes.dividedContent}>
            <div className={classes.dividedContentSection}>
              <b>{player?.parentNames || 'N/A'}</b>
            </div>
              
            {player?.parentContact && (
              <div className={classes.dividedContentSection}>
                <b>{player?.parentContact}</b>
              </div>
            )}
          </div>
        </ListItem>

        {player && (
          <ListItem
            icon={CoachIcon}
            title='Head Coach:'
            className={classes.listItem}
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>{player?.headCoach || 'N/A'}</b>
              </div>
              
              {player?.headCoachContact && (
                <div className={classes.dividedContentSection}>
                  <b>{player?.headCoachContact}</b>
                </div>
              )}
            </div>
          </ListItem>
        )}

        {player && (
          <ListItem
            icon={AgentIcon}
            title='Agent:'
            className={classes.listItem}
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>N/A</b>
              </div>
            </div>
          </ListItem>
        )}

        {player && (
          <ListItem
            icon={TrainerIcon}
            title='Trainer:'
            className={classes.listItem}
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>N/A</b>
              </div>
            </div>
          </ListItem>
        )}

        <ListItem
          icon={SchoolIcon}
          title='School:'
          className={classes.listItem}
        >
          <div className={classes.dividedContent}>
            <div className={classes.dividedContentSection}>
              <span><strong>{(primarySchool?.name || 'N/A')}</strong></span>
            </div>

            <div className={classes.dividedContentSection}>
              <span>GPA: <strong>{player?.gpa || 'N/A'}</strong> </span>
            </div>

            <div className={classes.dividedContentSection}>
              <span>SAT: <strong>{player?.sat || 'N/A'}</strong></span>
            </div>

            <div className={classes.dividedContentSection}>
              <span>ACT: <strong>{player?.act || 'N/A'}</strong></span>
            </div>
          </div>
        </ListItem>

        <ListItem
          icon={ClassIcon}
          title='Class:'
          className={classes.listItem}
        >
          <b>{(primarySchool && primarySchool.graduatingClass) || 0}</b>
        </ListItem>

        <ListItem
          icon={HomeIcon}
          title="Hometown:"
          className={classes.listItem}
        >
          <b>
            {primarySchool?.city}
            {primarySchool?.city && primarySchool?.state ? ', ' : ''}
            {primarySchool?.state}
          </b>
        </ListItem>
      </NamedBox>
    )
  }
  
  return (
    <NamedBox
      className={clsx(classes.playerProfileHighSchoolOverview, className)}
      headerNameClassName={classes.headerName}
      headerClassName={classes.headerClassName}
      name='High School Overview'
      headerContent={
        <Box className={classes.actionWrap}>
          <Button variant="contained" endIcon={<RecruitingIcon className={classes.actionIcon}/>}
          onClick={() => setIsRecruitingDialogOpen(true)} >
            Recruiting
          </Button>
          <Button variant="contained" endIcon={<ContactCardIcon className={classes.actionIcon}/>}
          onClick={() => setIsContactPlayerDialogOpen(true)}
          >
            Contact Player
          </Button>
        </Box>
      }
    >
      <Loader inProgress={loading} />

      <Grid container>
        {/* LEFT COLUMN */}
        <Grid item xs={12} sm={6} md={4} className={classes.column}>
          <ListItem icon={ClassIcon} title="Class:" className={classes.listItem}>
            <b>{(primarySchool && primarySchool.graduatingClass) || 0}</b>
          </ListItem>

          <ListItem icon={PositionIcon} title="Position:" className={classes.listItem}>
            <div className={classes.chipWithLabel}>
              <Chip label={selectedPosition} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
              <div className={classes.chipLabel}>(primary)</div>
            </div>
          </ListItem>

          <ListItem icon={RulerVerticalIcon} title="Height:" className={classes.listItem}>
            <b>{(primarySchool?.height ? inchesToFeetAndInches(primarySchool.height) : 'N/A')}</b>
          </ListItem>

          <ListItem icon={ScaleIcon} title="Weight:" className={classes.listItem}>
            <b>{primarySchool?.weight} lbs.</b>
          </ListItem>

          <ListItem icon={SchoolIcon} title="High School:" className={classes.listItem}>
            <b>{primarySchool?.name}</b>
          </ListItem>

          <ListItem icon={HomeIcon} title="Hometown:" className={classes.listItem}>            
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>
                  {primarySchool?.city}
                  {primarySchool?.city && primarySchool?.state ? ', ' : ''}
                  {primarySchool?.state}
                </b>
              </div>

              <div className={classes.dividedContentSection}>
                <CanadaIcon className={classes.locationIcon} />
              </div>

              <div className={classes.dividedContentSection}>
                <GlobeIcon className={classes.locationIcon} />
              </div>
            </div>
          </ListItem>
        </Grid>

        {/* MIDDLE COLUMN */}
        <Grid item xs={12} sm={6} md={4} className={classes.column}>
          <ListItem icon={JerseyIcon} title="Jersey:" className={classes.listItem}>
            {/* {player?.hsPosition && <Chip label={7} variant="outlined" className={classes.chip} />} */}
            <Chip label={7} variant="outlined" className={classes.chip} />
          </ListItem>

          <ListItem icon={ThreeX} title="TF Triple Verified&reg;:" className={classes.listItem}>
            <div className={classes.badges}>
              {(player?.pai?.length ?? 0) > 0 && (
                <div className={classes.badge}>
                  <PAIFilledIcon className={classes.badgeIcon} />
                  PAI
                </div>
              )}
              
              {(player?.combine?.length ?? 0) > 0 && (
                <div className={classes.badge}>
                  <COMFilledIcon className={classes.badgeIcon} />
                  COM
                </div>
              )}

              {(player?.reelMPH || player?.bestHighSchoolMPH?.mph) && (
                <div className={classes.badge}>
                  <MPHFilledIcon className={classes.badgeIcon} />
                  MPH
                </div>
              )}
            </div>
          </ListItem>

          <ListItem icon={TypesIcon} title="Player Types:" className={classes.listItem}>
            <div className={classes.badges}>
              {/* {player?.isSpeedRecruit && <span className={classes.badge}>Speed</span>}
              {player?.isPowerRecruit && <span className={classes.badge}>Power</span>}
              {(player?.multiSport?.length ?? 0) >= 3 && <span className={classes.badge}>3-Sport</span>} */}

              <div className={classes.badge}>
                <CheckCircleIcon className={clsx(classes.badgeIcon, classes.badgeIconOrange)} />
                Speed
              </div>

              <div className={classes.badge}>
                <CheckCircleIcon className={clsx(classes.badgeIcon, classes.badgeIconGreen)} />
                Power
              </div>

              <div className={classes.badge}>
                <CheckCircleIcon className={classes.badgeIcon} />
                3-Sport
              </div>

              {/* <div className={classes.badge}>
                <CheckCircleIcon className={clsx(classes.badgeIcon, classes.badgeIconGray)} />
                N/A
              </div> */}
            </div>
          </ListItem>

          <ListItem icon={RugbyIcon} title="Composite:" className={classes.listItem}>
            <b>{player?._247Comp}</b>
          </ListItem>

          <ListItem icon={StarIcon} title="Stars:" className={classes.listItem}>
            <Stars value={player?._247Star ?? player?.compStar ?? 0} />
          </ListItem>

          {(!!links?.length) && (
            <ListItem
              icon={UserCircleIcon}
              title='Social Profiles:'
              className={classes.listItem}
            >
              <Links links={allLinks} />
            </ListItem>
          )}
        </Grid>

        {/* RIGHT COLUMN */}
        <Grid item xs={12} md={4}>
          <ListItem icon={ReportCardIcon} title="Academics:" className={classes.listItem}>
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <span>GPA: <strong>{player?.gpa || 'N/A'}</strong> </span>
              </div>

              <div className={classes.dividedContentSection}>
                <span>SAT: <strong>{player?.sat || 'N/A'}</strong></span>
              </div>

              <div className={classes.dividedContentSection}>
                <span>ACT: <strong>{player?.act || 'N/A'}</strong></span>
              </div>
            </div>
          </ListItem>

          <ListItem icon={OffersIcon} title="Offers:" className={classes.listItem}>
            <Chip label={player?.offers?.length || 0} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
          </ListItem>

          {player?.committedTo ? (
            <ListItem icon={HandshakeIcon} title="Committed:" className={classes.listItem}>
              <Box display="flex" alignItems="center">
                <div className={classes.committedTeam}>
                  <TeamLogo team={player?.committedTo} className={classes.committedTeamLogo} />
                  <b>{player?.committedTo.name}</b>
                </div>
              </Box>
            </ListItem>
          ): (
            <ListItem icon={HandshakeIcon} title="Committed:" className={classes.listItem}>
              <Box display="flex" alignItems="center">
                <b>Uncommitted</b>
              </Box>
            </ListItem>
            )
          }

          <ListItem
            icon={On3LogoIcon}
            title='Total Player Value:'
            className={classes.listItem}
          >
            {
              player?.on3PublicNILValuation ? 
                <>
                  <b>${on3PublicNILVal}</b>
                  <NilValuesTooltip playerNilValues={playerNilValues} />
                  <b className={classes.leftMargin}>{playerOn3Date && datetimeToDate(playerOn3Date)}</b>
                </>
              : <NotAvailable />
            }
          </ListItem>

          <ListItem icon={TFPillLogoIcon} title="TFV Value:" className={classes.listItem}>
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                <b>{player?.tfvs || <NotAvailable />}</b>
              </div>

              <div className={classes.dividedContentSection}>
                <div className={classes.tfvValue}>
                  <TFVLogoIcon className={classes.tfvIcon} />
                  <span>Score: <strong>4.0</strong></span>
                </div>
              </div>
            </div>
          </ListItem>
          
          {/* socialLinks */}
          {(!!links?.length) && (
            <ListItem
              icon={LinkIcon}
              title='Social Profiles:'
              className={classes.listItem}
            >
              <Links links={socialLinks} linkClassName={classes.socialProfile} />
            </ListItem>
          )}
        </Grid>
      </Grid>

      <Dialog 
        open={isRecruitingDialogOpen}
        title='Recruiting'
        onClose={() => setIsRecruitingDialogOpen(false)}
      >
        {/* {recruitingContent()} */}
        <RecruitingContent primarySchool={primarySchool} player={player} links={links} committedToTeam={committedToTeam} />
      </Dialog>

      <Dialog 
        open={isContactPlayerDialogOpen}
        title='Contact Player'
        onClose={() => setIsContactPlayerDialogOpen(false)}
      >
        {contactPlayerContent()}
      </Dialog>

    </NamedBox>
  )
}
